import { createReducer, createSelector } from '@reduxjs/toolkit';
import { all, call, fork, takeEvery } from 'redux-saga/effects';
import {
  getAllInvitationGroupsInitialize,
  getAllInvitationGroupsError,
  getAllInvitationGroupsSuccess,
  updateInvitationGroupInitialize,
  createInvitationGroupInitialize,
  deleteInvitationGroupInitialize,
  createInvitationGroupSuccess,
  updateInvitationGroupSuccess,
  deleteInvitationGroupSuccess,
} from '../actions/api';
import { removeItemFromList, updateDtoList } from '../helpers/reducerHelpers';
import {
  generateCreateApiCall,
  generateGetAllApiCall,
  generateUpdateApiCall,
} from '../helpers/sagaHelpers';
import {
  getInitActionPayload,
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import { InvitationGroupConnector } from '../connectors/invitationGroupConnector';
import { hearingTypeByIdSelector } from './hearingTypeSlice';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import { allInvitationGroupMappingsSelector } from './invitationGroupMappingSlice';
import { HEARINGTYPE_TYPE, INVITATIONGROUP_TYPE } from '../../constants/api';
import { API_REDUCER } from '../../constants/store';

// -------- Overview --------

// The following endpoints exist
//  - GET /InvitationGroup
//  - POST /InvitationGroup
//  - PATCH /InvitationGroup/{id}
//  - DELETE /InvitationGroup/{id}
//  - PATCH /{invitationGroupId}/InvitationKeys
//
// InvitationGroup is included in the following endpoints
//  - No endpoints include InvitationGropups

// -------- Reducer --------
const invitationGroupReducer = createReducer(
  {
    all: [],
    isFetchingInvitationGroups: false,
  },
  (builder) => {
    builder
      .addCase(getAllInvitationGroupsInitialize, (state) => {
        state.isFetchingInvitationGroups = true;
      })
      .addCase(getAllInvitationGroupsError, (state) => {
        state.isFetchingInvitationGroups = false;
      })
      .addCase(getAllInvitationGroupsSuccess, (state, action) => {
        const newState = Object.assign({}, state, {
          isFetchingInvitationGroups: false,
        });
        const invitationGroups = getRequestData(action);
        return updateDtoList(newState, 'all', invitationGroups);
      })
      .addCase(createInvitationGroupSuccess, (state, action) => {
        const invitationGroup = getRequestData(action);
        return updateDtoList(state, 'all', invitationGroup);
      })
      .addCase(updateInvitationGroupSuccess, (state, action) => {
        const invitationGroup = getRequestData(action);
        return updateDtoList(state, 'all', invitationGroup);
      })
      .addCase(deleteInvitationGroupSuccess, (state, action) => {
        const invitationGroupId = getInitActionPayload(action);
        return removeItemFromList(state, 'all', invitationGroupId);
      });
  },
);

// -------- Sagas ---------
export function* invitationGroupSaga() {
  yield all([
    fork(getAllInvitationGroupsSaga),
    fork(updateInvitationGroupSaga),
    fork(createInvitationGroupSaga),
    fork(deleteInvitationGroupSaga),
  ]);
}

function* getAllInvitationGroupsSaga() {
  yield takeEvery(
    getAllInvitationGroupsInitialize.type,
    generateGetAllApiCall(InvitationGroupConnector, function* (connector) {
      return yield call([connector, connector.getInvitationGroups]);
    }),
  );
}

function* createInvitationGroupSaga() {
  yield takeEvery(
    createInvitationGroupInitialize.type,
    generateCreateApiCall(InvitationGroupConnector, function* (
      connector,
      action,
    ) {
      return yield call(
        [connector, connector.createInvitationGroup],
        action.payload,
      );
    }),
  );
}

function* updateInvitationGroupSaga() {
  yield takeEvery(
    updateInvitationGroupInitialize.type,
    generateUpdateApiCall(InvitationGroupConnector, function* (
      connector,
      action,
      requestData,
    ) {
      return yield call(
        [connector, connector.updateInvitationGroup],
        action.payload.invitationGroupId,
        requestData,
      );
    }),
  );
}

function* deleteInvitationGroupSaga() {
  yield takeEvery(
    deleteInvitationGroupInitialize.type,
    generateUpdateApiCall(InvitationGroupConnector, function* (
      connector,
      action,
    ) {
      return yield call(
        [connector, connector.deleteInvitationGroup],
        action.payload,
      );
    }),
  );
}

// -------- Selectors --------
const selectGetAllInvitationGroups = (state) =>
  selectAllFromApiState(state, API_REDUCER.INVITATION_GROUP);

const allInvitationGroupsSelector = createSelector(
  selectGetAllInvitationGroups,
  (invitationGroups) => invitationGroups,
);

const invitationGroupsByHearingTypeIdSelector = (hearingTypeId) => {
  return createSelector(
    [
      allInvitationGroupsSelector,
      hearingTypeByIdSelector(hearingTypeId),
      allInvitationGroupMappingsSelector,
    ],
    (invitationGroups, hearingType, invitationGroupMappings) =>
      invitationGroups.filter((invitationGroup) =>
        invitationGroupMappings?.some(
          (invitationGroupMapping) =>
            getRelId(invitationGroupMapping, INVITATIONGROUP_TYPE) ===
              getEntityId(invitationGroup) &&
            getRelId(invitationGroupMapping, HEARINGTYPE_TYPE) ===
              getEntityId(hearingType),
        ),
      ),
  );
};

const invitationGroupByIdSelector = (invitationGroupId) => {
  return createSelector(allInvitationGroupsSelector, (invitationGroups) =>
    invitationGroups.find(
      (invitationGroup) => getEntityId(invitationGroup) === invitationGroupId,
    ),
  );
};

const invitationGroupState = (state) => state.api.invitationGroup;

const isFetchingInvitationGroupsSelector = createSelector(
  invitationGroupState,
  (state) => state.isFetchingInvitationGroups,
);

// -------- Exporting --------
export {
  allInvitationGroupsSelector,
  invitationGroupsByHearingTypeIdSelector,
  invitationGroupByIdSelector,
  isFetchingInvitationGroupsSelector,
};

export default invitationGroupReducer;
